import { call, put, takeEvery, delay } from 'typed-redux-saga';
import { toast } from 'helpers/toast';
import {
	uploadLMSFile,
	getBulkOperationStatus,
	downloadLMSTemplate,
	downloadLMSBulkUploadBlankTemplate,
	BulkUploadResponse,
	BulkOperationStatus,
	BulkOperationStatusResponse,
} from 'services/lms/lmsBulkUpload.service';
import * as actions from 'reducers/lms/bulkUpload/bulkUpload.action';
import { ActionType } from 'typesafe-actions';
import i18n from 'i18n';

function* uploadLMSFileSaga(action: ActionType<typeof actions.uploadLMSFile.request>): Generator {
	try {
		const response = yield* call(uploadLMSFile, action.payload.file);
		yield* put(actions.uploadLMSFile.success(response));
		yield* put(actions.getBulkOperationStatus.request({ bulkOperationId: response.data }));

		toast.success(i18n.t('message.lms.bulkUpload.upload.success'));
	} catch (error) {
		const errorMessage = (error as any)?.message || 'Upload failed';
		yield* put(actions.uploadLMSFile.failure(errorMessage));
		toast.error(errorMessage);
	}
}

function* getBulkOperationStatusSaga(action: ActionType<typeof actions.getBulkOperationStatus.request>): Generator {
	try {
		const response = yield* call(getBulkOperationStatus, action.payload.bulkOperationId);
		yield* put(actions.getBulkOperationStatus.success(response));

		if (!response.data) {
			toast.error(i18n.t('message.lms.bulkUpload.operation.failed'));
			yield* put(actions.getBulkOperationStatus.failure('No data received'));
			return;
		}
		const { stage } = response.data;

		if (['uploading', 'validating', 'creating-entities'].includes(stage)) {
			yield* delay(2000);
			yield* put(actions.getBulkOperationStatus.request({ bulkOperationId: action.payload.bulkOperationId }));
		} else if (stage === 'done') {
			toast.success(i18n.t('message.lms.bulkUpload.operation.success'));
		} else if (stage === 'validation-failed') {
			toast.error(i18n.t('message.lms.bulkUpload.validation.failed'));
		} else if (stage === 'failed') {
			toast.error(i18n.t('message.lms.bulkUpload.operation.failed'));
		}
	} catch (error) {
		const errorMessage = (error as any)?.message || i18n.t('message.lms.bulkUpload.operation.failed');
		yield* put(actions.getBulkOperationStatus.failure(errorMessage));
		toast.error(errorMessage);
	}
}

function* downloadLMSTemplateSaga(action: ActionType<typeof actions.downloadLMSTemplate.request>): Generator {
	try {
		const { templateType } = action.payload;
		const response =
			templateType === 'add' ? yield* call(downloadLMSBulkUploadBlankTemplate) : yield* call(downloadLMSTemplate);

		yield* put(actions.downloadLMSTemplate.success(response));

		const link = document.createElement('a');
		link.href = response.data;
		link.download = templateType === 'add' ? 'lms-bulk-upload-template.xlsx' : 'lms-bulk-template.xlsx';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		toast.success(i18n.t('message.lms.bulkUpload.template.download.success'));
	} catch (error) {
		const errorMessage = (error as any)?.message || 'Failed to download template';
		yield* put(actions.downloadLMSTemplate.failure(errorMessage));
		toast.error(errorMessage);
	}
}

export default function* bulkUploadSaga() {
	yield* takeEvery(actions.uploadLMSFile.request, uploadLMSFileSaga);
	yield* takeEvery(actions.getBulkOperationStatus.request, getBulkOperationStatusSaga);
	yield* takeEvery(actions.downloadLMSTemplate.request, downloadLMSTemplateSaga);
}
